import backgroundVideoUrl from 'assets/videos/video-transition-wave-blur-bigger.mp4';
import {
  type FunctionComponent, useEffect, useRef, useState,
} from 'react';

import { Link, newAsyncDataEnvelope } from '@dr.pogodin/react-utils';

import GlassCard from 'components/GlassCard';

import PageWithVideoBackground from 'components/PageWithVideoBackground';
import { usePastSessions } from 'hooks/usePastSessions';
import { useUserScores } from 'hooks/useUserScores';
import { useVideos } from 'hooks/video';

import { P_AIAPP_ONBOARDING } from 'utils';

import {
  type QuestionnaireStateT,
  initExperienceData,
  useGlobalState,
  useGlobalStateObject,
} from 'utils/global-state';

import { QuestionnaireKinds } from 'utils/questionnaire';

import IconAudioSettings from 'assets/images/ai/icon-audio-settings.svg';

import IconOnboard from 'assets/images/ai/icon-onboard.svg';
import IconType from 'assets/images/ai/icon-type.svg';
import backgroundImageUrl from 'assets/images/bg-transitions-blur.png';
import IconPlus from 'assets/images/icon-plus-24px.svg';

import flexGlassTheme from './glass-flex.scss';

import './style.scss';

const HomePage: FunctionComponent = () => {
  const gs = useGlobalStateObject();

  const [user] = useGlobalState('user');
  if (!user) throw Error('Unauthenticated');

  const [, setAudioSetup] = useGlobalState('ui.audioSetup');

  const pastSessions = usePastSessions();
  useUserScores();
  useVideos({ query: { asEndUser: true } });

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const startFreshCheckIn = () => {
    gs.set('experience', newAsyncDataEnvelope({
      checkInId: null,
      experience: initExperienceData(),
      sessionKind: null,
    }, {
      timestamp: Date.now(),
    }));
    gs.set(
      'questionnaire',
      newAsyncDataEnvelope<QuestionnaireStateT>({
        data: {
          items: [],
          kind: QuestionnaireKinds.WELLNESS_CHECKIN__AI_REALTIME,
          numQuestionsExpected: 0,
          startedAt: Date.now(),
          transcriptId: null,
          userId: user.id,
        },
        nextQuestionIdx: 0,
      }),
    );
  };

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocPointerDown = (e: PointerEvent): void => {
      const el = menuRef.current;
      if (el && !el.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('pointerdown', onDocPointerDown);
    return () => {
      document.removeEventListener('pointerdown', onDocPointerDown);
    };
  }, [menuOpen]);

  return (
    <PageWithVideoBackground
      fallbackImageUrl={backgroundImageUrl}
      videoUrl={backgroundVideoUrl}
    >
      <div styleName="page">
        <div ref={menuRef} styleName="mobileMenu">
          <button
            aria-label="New experience"
            onClick={() => {
              setMenuOpen((prev) => !prev);
            }}
            styleName="plusButton"
            type="button"
          >
            <IconPlus />
          </button>
          {menuOpen ? (
            <div styleName="flyout">
              <Link
                onClick={() => {
                  setMenuOpen(false);
                  startFreshCheckIn();
                }}
                styleName="flyoutLink"
                to="experience"
              >
                Create a New Experience
              </Link>
              {pastSessions.sessions.length ? (
                <Link
                  onClick={() => {
                    setMenuOpen(false);
                  }}
                  styleName="flyoutLink"
                  to="past-sessions"
                >
                  My Previous Experiences
                </Link>
              ) : null}
            </div>
          ) : null}
        </div>
        {user.onboardingDone ? null : (
          <div styleName="welcomeBanner">
            <GlassCard theme={flexGlassTheme}>
              <div styleName="welcomeSection">
                <div styleName="welcomeSectionContent">
                  <h2 styleName="welcomeSectionTitle">
                    Welcome to Soul Sanctuary
                  </h2>
                  <p styleName="welcomeSectionText">
                    Before I begin recommending experiences, I&apos;d love to
                    get to know you a little better. We&apos;ll keep it to just
                    a few minutes.
                  </p>
                </div>
                <div styleName="welcomeSectionButtonWrapper">
                  <Link styleName="onboardButton" to={P_AIAPP_ONBOARDING}>
                    <IconOnboard />
                    Start Onboarding
                  </Link>
                </div>
              </div>
            </GlassCard>
          </div>
        )}
        <div styleName="mainContent">
          <div styleName="welcomeContent">
            <h2 styleName="welcomeTitle">
              How are you <br />
              feeling today
            </h2>
          </div>
          <Link
            onClick={startFreshCheckIn}
            styleName="onboardButton"
            to="check-in"
          >
            <IconOnboard />
            Wellness Check-in
          </Link>
          <Link
            onClick={startFreshCheckIn}
            styleName="inlineLink"
            to="/check-in?mode=text"
          >
            <IconType />
            Type Instead
          </Link>
        </div>
        <button
          onClick={() => {
            setAudioSetup(true);
          }}
          styleName="inlineLink audioSettingLink"
          type="button"
        >
          <IconAudioSettings />
          Audio Setting
        </button>
      </div>
    </PageWithVideoBackground>
  );
};

export default HomePage;
